// ---------------------------------------------------------------------------
// reviewQuality.js
// Owner-supplied evidence. Everything here comes from the campaign owner's
// form inputs; nothing is inferred or invented. Pages only reference
// evidence that exists, and video moments only appear when the owner
// typed real timestamps.
// ---------------------------------------------------------------------------

const safeUrl=(value)=> {
  try {
    const url=new URL(value);
    return ['https:','http:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
};

const lines=(text)=> String(text || '').split(/\r?\n/).map((l)=> l.trim()).filter(Boolean);

// "https://.../dashboard.png | Campaign dashboard after setup"
const parseImages=(text)=> lines(text).map((line)=> {
  const [url,...caption]=line.split('|');
  const href=safeUrl(url.trim());
  return href ? {url: href,caption: caption.join('|').trim()} : null;
}).filter(Boolean).slice(0,10);

// "02:08 | Campaign creation: target URL and keyword fields are filled in"
const parseVideoNotes=(text)=> lines(text).map((line)=> {
  const match=line.match(/^\s*((?:\d{1,2}:)?\d{1,2}:\d{2})\s*[|\-–—:]\s*(.+)$/);
  if (!match) return null;
  const parts=match[1].split(':').map(Number);
  const seconds=parts.reduce((total,n)=> total * 60 + n,0);
  return {time: match[1],seconds,description: match[2].trim()};
}).filter(Boolean).sort((a,b)=> a.seconds - b.seconds).slice(0,15);

export const buildEvidenceContext=({
  testNotes='',metricNotes='',evidenceUrls='',imageEvidence='',videoNotes='',docExcerpts='',
  affiliateDisclosure=false,verifiedVideoUrl='',
}={})=> ({
  testNotes: String(testNotes).trim().slice(0,4000),
  metricNotes: String(metricNotes).trim().slice(0,4000),
  docExcerpts: String(docExcerpts).trim().slice(0,4000),
  links: lines(evidenceUrls).map(safeUrl).filter(Boolean).slice(0,8),
  images: parseImages(imageEvidence),
  videoMoments: parseVideoNotes(videoNotes),
  videoUrl: safeUrl(String(verifiedVideoUrl).trim()),
  affiliate: Boolean(affiliateDisclosure),
});

export const hasEvidence=(e)=> Boolean(e && (e.testNotes || e.metricNotes || e.docExcerpts || e.links.length || e.images.length || e.videoMoments.length));

/** Evidence summary for the writing prompt (owner data, clearly labelled). */
export const evidenceForPrompt=(e)=> {
  if (!hasEvidence(e)) return 'NO OWNER EVIDENCE SUPPLIED: there is no hands-on testing, screenshots or measured data. Do not imply any.';
  return [
    'OWNER-SUPPLIED EVIDENCE (real; reference it directly and attribute it to the reviewer\'s own testing):',
    e.testNotes && `What was tested: ${e.testNotes}`,
    e.metricNotes && `Measured results (the ONLY verified figures): ${e.metricNotes}`,
    e.docExcerpts && `Documentation excerpts: ${e.docExcerpts}`,
    e.images.length && `Screenshots provided: ${e.images.map((i)=> i.caption || 'screenshot').join('; ')}`,
    e.videoMoments.length && `Video moments (real timestamps): ${e.videoMoments.map((m)=> `${m.time} ${m.description}`).join('; ')}`,
    e.links.length && `Supporting links: ${e.links.join(', ')}`,
  ].filter(Boolean).join('\n');
};
